import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Sparkles, Crown } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const BetaWaiting = () => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [betaStatus, setBetaStatus] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!token) {
      navigate('/auth');
    }
  }, [token, navigate]);

  // Fetch beta status
  useEffect(() => {
    const fetchBetaStatus = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/beta/user-status`, { headers,  });
        setBetaStatus(response.data);
        
        // If user can access the app, redirect to onboarding or dashboard
        if (response.data.can_access_app) {
          if (user?.onboarding_completed) {
            navigate('/dashboard');
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error('Error fetching beta status:', error);
      }
    };
    
    if (token) {
      fetchBetaStatus();
    }
  }, [token, user, navigate]);

  // Countdown timer
  useEffect(() => {
    if (!betaStatus?.launch_date) return;

    const targetDate = new Date(betaStatus.launch_date);

    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      
      if (diff <= 0) {
        setTimeLeft(null);
        // Refresh status when countdown ends
        window.location.reload();
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [betaStatus]);

  const messages = {
    fr: {
      // Pre-launch message (avant le 15 avril)
      prelaunchTitle: "Tu es à ta place.",
      prelaunchSubtitle: "Ce que tu as rejoint n'est pas un hasard.",
      prelaunchDescription: "Vision Lab vous ouvrira ses portes en exclusivité le 15 avril.",
      prelaunchClosing: "D'ici là… laisse cette vision grandir en toi.",
      countdownIntro: "Le compte à rebours a commencé.",
      yourPlace: "Ta place est réservée",
      foundingMember: "Founding Member",
      benefitsIntro: "Accès complet à Vision Lab",
      benefitsDescription: "Une expérience pensée pour transformer ta vision",
      benefitsTeaser: "Des privilèges réservés aux Founding Members (révélés en temps voulu)",
      countdown: {
        days: "Jours",
        hours: "Heures",
        minutes: "Min",
        seconds: "Sec"
      },
      seeYouSoon: "On se retrouve très bientôt.",
      signature: "— Manuela"
    },
    en: {
      prelaunchTitle: "You are exactly where you belong.",
      prelaunchSubtitle: "What you've joined is not a coincidence.",
      prelaunchDescription: "Vision Lab will open its doors exclusively to you on April 15th.",
      prelaunchClosing: "Until then… let this vision grow within you.",
      countdownIntro: "The countdown has begun.",
      yourPlace: "Your spot is reserved",
      foundingMember: "Founding Member",
      benefitsIntro: "Full access to Vision Lab",
      benefitsDescription: "An experience designed to transform your vision",
      benefitsTeaser: "Exclusive privileges reserved for Founding Members (revealed in due time)",
      countdown: {
        days: "Days",
        hours: "Hours",
        minutes: "Min",
        seconds: "Sec"
      },
      seeYouSoon: "See you very soon.",
      signature: "— Manuela"
    },
    pt: {
      prelaunchTitle: "Você está no lugar certo.",
      prelaunchSubtitle: "O que você encontrou não é coincidência.",
      prelaunchDescription: "Vision Lab abrirá suas portas exclusivamente para você no dia 15 de abril.",
      prelaunchClosing: "Até lá… deixe essa visão crescer dentro de você.",
      countdownIntro: "A contagem regressiva começou.",
      yourPlace: "Seu lugar está reservado",
      foundingMember: "Founding Member",
      benefitsIntro: "Acesso completo ao Vision Lab",
      benefitsDescription: "Uma experiência pensada para transformar sua visão",
      benefitsTeaser: "Privilégios exclusivos reservados aos Founding Members (revelados no momento certo)",
      countdown: {
        days: "Dias",
        hours: "Horas",
        minutes: "Min",
        seconds: "Seg"
      },
      seeYouSoon: "Nos vemos muito em breve.",
      signature: "— Manuela"
    }
  };

  const lang = i18n.language?.substring(0, 2) || 'fr';
  const content = messages[lang] || messages.fr;

  return (
    <div className="min-h-screen bg-[#030303] flex flex-col">
      {/* Language Selector */}
      <div className="absolute top-6 right-6 z-50">
        <LanguageSelector variant="minimal" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl w-full text-center"
        >
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="mb-8 inline-block"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-transparent border border-[#D4AF37]/30 flex items-center justify-center">
              <Sparkles className="text-[#D4AF37]" size={36} />
            </div>
          </motion.div>

          {/* Title - Emotional Opening */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl text-white mb-4"
          >
            {content.prelaunchTitle}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-[#A1A1AA] text-lg md:text-xl mb-6 italic"
          >
            {content.prelaunchSubtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-[#D4AF37] text-xl md:text-2xl font-serif mb-8"
          >
            {content.prelaunchDescription}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="text-[#71717A] text-base mb-4 italic"
          >
            {content.prelaunchClosing}
          </motion.p>

          {/* Countdown Intro */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-white/60 text-sm tracking-[0.2em] uppercase mb-6"
          >
            {content.countdownIntro}
          </motion.p>

          {/* Countdown */}
          {timeLeft && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65 }}
              className="mb-10"
            >
              <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
                {[
                  { value: timeLeft.days, label: content.countdown.days },
                  { value: timeLeft.hours, label: content.countdown.hours },
                  { value: timeLeft.minutes, label: content.countdown.minutes },
                  { value: timeLeft.seconds, label: content.countdown.seconds }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 p-4">
                      <motion.span 
                        key={item.value}
                        initial={{ scale: 1.1 }}
                        animate={{ scale: 1 }}
                        className="block text-3xl md:text-4xl font-serif text-[#D4AF37]"
                      >
                        {String(item.value).padStart(2, '0')}
                      </motion.span>
                    </div>
                    <span className="text-[#71717A] text-xs mt-2 block tracking-wider uppercase">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Divider */}
          <div className="w-16 h-px bg-[#D4AF37]/30 mx-auto mb-8" />

          {/* Your Place Reserved Card - Minimal Premium Version */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-8 mb-8"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Crown className="text-[#D4AF37]" size={24} />
              <span className="text-[#D4AF37] text-sm tracking-widest uppercase">
                {content.foundingMember}
              </span>
            </div>
            
            <p className="text-white text-xl font-serif mb-4">
              {content.yourPlace}
            </p>
            
            <div className="space-y-3 text-center max-w-sm mx-auto">
              <p className="text-white text-base">
                {content.benefitsIntro}
              </p>
              <p className="text-[#A1A1AA] text-sm italic">
                {content.benefitsDescription}
              </p>
              <p className="text-[#71717A] text-xs mt-4">
                {content.benefitsTeaser}
              </p>
            </div>
          </motion.div>

          {/* Signature */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-[#71717A]"
          >
            <p className="mb-2">{content.seeYouSoon}</p>
            <p className="text-[#D4AF37] font-serif italic">{content.signature}</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-[#52525B] text-xs">
        <p>Clierxter Vision Lab</p>
      </div>
    </div>
  );
};

export default BetaWaiting;
